import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Check, ChevronLeft, ChevronRight, Play } from 'lucide-react'
import SectionTitle from '../components/SectionTitle'
import { audiences, capabilities, heroSlides, learningLevels } from '../data'

function Hero() {
  const [active, setActive] = useState(0)
  const slide = heroSlides[active]

  useEffect(() => {
    const timer = window.setInterval(() => setActive((current) => (current + 1) % heroSlides.length), 6500)
    return () => window.clearInterval(timer)
  }, [])

  const changeSlide = (next) => setActive((next + heroSlides.length) % heroSlides.length)

  return (
    <section className="hero" aria-roledescription="carousel" aria-label="Qubispark training highlights">
      <div className="container hero__frame">
        <div className="hero__copy" key={`copy-${active}`}>
          <span className="eyebrow">{slide.eyebrow}</span>
          <h1>{slide.title}</h1>
          <p>{slide.text}</p>
          <div className="hero__actions">
            <Link className="button" to={slide.primaryTo}>{slide.primary} <ArrowUpRight size={18} /></Link>
            <Link className="button button--ghost" to={slide.secondaryTo}>{slide.secondary}</Link>
          </div>
          <div className="hero__controls">
            <button onClick={() => changeSlide(active - 1)} aria-label="Previous banner"><ChevronLeft size={18} /></button>
            <div className="hero__dots">
              {heroSlides.map((item, index) => (
                <button key={item.title} className={active === index ? 'active' : ''} onClick={() => setActive(index)} aria-label={`Show banner ${index + 1}`} aria-current={active === index ? 'true' : undefined} />
              ))}
            </div>
            <button onClick={() => changeSlide(active + 1)} aria-label="Next banner"><ChevronRight size={18} /></button>
            <span>0{active + 1} / 0{heroSlides.length}</span>
          </div>
        </div>
        <div className="hero__image" key={`image-${active}`}>
          <img src={slide.image} alt={slide.alt} />
          <div className="hero__image-note">
            <span className="pulse"><Play size={13} fill="currentColor" /></span>
            <p><strong>Learn by doing</strong><small>Live demos · Guided labs · Real projects</small></p>
          </div>
        </div>
      </div>
      <div className="container trust-strip">
        <span>Learning experiences for</span>
        <strong>Students</strong><i /> <strong>Educators</strong><i /> <strong>Institutions</strong><i /> <strong>Corporate teams</strong>
      </div>
    </section>
  )
}

export default function Home() {
  return (
    <>
      <Hero />

      <section className="section intro-section">
        <div className="container intro-grid">
          <div>
            <span className="eyebrow">Technology becomes capability here</span>
            <h2>Training designed for the people who will shape what’s next.</h2>
          </div>
          <div className="intro-copy">
            <p>Qubispark Technologies delivers sophisticated, accessible training in AI and emerging technologies. We help learners move from understanding the landscape to applying tools and building solutions with confidence.</p>
            <Link to="/about" className="text-link">Our story and purpose <ArrowRight size={18} /></Link>
          </div>
        </div>
        <div className="container audience-grid">
          {audiences.map(({ icon: Icon, title, text }, index) => (
            <article className="audience-card" key={title}>
              <div className="icon-box"><Icon size={23} /></div><span>0{index + 1}</span>
              <h3>{title}</h3><p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section capabilities-section">
        <div className="container">
          <SectionTitle eyebrow="What you can learn" title="Modern skills. Practical depth. Lasting confidence." text="Choose focused workshops or combine topics into an end-to-end learning pathway." align="center" />
          <div className="capability-grid">
            {capabilities.map(({ icon: Icon, title, text }) => (
              <article className="capability-card" key={title}>
                <Icon size={25} /><div><h3>{title}</h3><p>{text}</p></div><ArrowUpRight className="card-arrow" size={20} />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section journey-section">
        <div className="container">
          <SectionTitle eyebrow="A connected learning journey" title="Progress through capability—not packages." text="Each level describes the depth of learning and can be adapted to the audience, time available and desired outcome." />
          <div className="journey-list">
            {learningLevels.map((level) => (
              <article className="journey-row" key={level.label}>
                <div className="journey-row__number">{level.number}</div>
                <div><span className="level-label">{level.label}</span><h3>{level.title}</h3></div>
                <p>{level.text}</p>
                <ul>{level.outcomes.slice(0, 3).map((item) => <li key={item}><Check size={15} /> {item}</li>)}</ul>
              </article>
            ))}
          </div>
          <Link to="/programs" className="button button--ghost button--center">View the complete curriculum <ArrowRight size={18} /></Link>
        </div>
      </section>

      <section className="section taksha-feature">
        <div className="container taksha-card">
          <div className="taksha-card__copy">
            <span className="eyebrow">Built by Qubispark</span>
            <h2>Meet Taksha AI—your learning companion.</h2>
            <p>A focused AI learning platform that helps students and teachers explore concepts, practise skills and make steady progress in one connected environment.</p>
            <Link to="/taksha-ai" className="button">Explore Taksha AI <ArrowUpRight size={18} /></Link>
          </div>
          <div className="taksha-ui" aria-label="Taksha AI product preview">
            <div className="taksha-ui__top"><span /><span /><span /><strong>taksha.ai</strong></div>
            <div className="taksha-ui__body">
              <div className="taksha-sidebar"><b>T</b><i /><i /><i /><i /></div>
              <div className="taksha-content">
                <span className="mini-label">TODAY'S LEARNING PATH</span>
                <h3>Welcome back, learner.</h3>
                <div className="progress-card"><div><span>Generative AI Foundations</span><strong>72%</strong></div><i><b /></i></div>
                <div className="lesson-grid"><div><Play size={18} /><b>Prompt patterns</b><small>12 min lesson</small></div><div><Check size={18} /><b>Knowledge check</b><small>5 questions</small></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section outcome-section">
        <div className="container outcome-grid">
          <div className="outcome-image"><img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1400&q=88" alt="Learner working on a laptop" /></div>
          <div className="outcome-copy">
            <SectionTitle eyebrow="What we build with you" title="Capability that continues after the session ends." />
            <div className="outcome-list">
              <div><strong>01</strong><p><b>Practical confidence</b><span>People understand where to begin, how to experiment and how to keep learning.</span></p></div>
              <div><strong>02</strong><p><b>Useful workflows</b><span>Learners leave with repeatable ways to improve research, creation and execution.</span></p></div>
              <div><strong>03</strong><p><b>Visible outcomes</b><span>Projects and demonstrations make progress tangible for learners and sponsors.</span></p></div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
